import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, combineLatest, firstValueFrom, takeUntil } from 'rxjs';
import {
  ApiFieldDataOption,
  ApiFieldConstraint,
  ApiNestedProduct,
  ApiProductCatalogItem,
  ApiProductField,
  ApiProductPage,
} from '../../../../core/models/product-api.models';
import {
  ApiDetailQuittance,
  ApiGarantie,
  ApiRachat,
  serializeDetailQuittanceForAfn,
} from '../../../../core/models/devis-api.models';
import { ApiInitData } from '../../../../core/models/init-data.models';
import {
  emptyBankDetails,
  formatBankDetails,
  isBankDetailsValue,
  mapCustomerBanks,
  parseBankDetails,
} from '../../../../core/models/bank-details.models';
import { UserProfile } from '../../../../core/models/auth.models';
import { ProductApiService } from '../../../../core/services/product-api.service';
import { SouscriptionApiService } from '../../../../core/services/souscription-api.service';
import { AuthService } from '../../../../core/services/auth.service';
import { extractApiErrorMessage } from '../../../../core/utils/api-error.util';
import {
  AssureRecord,
  getBottomSheetFields,
  validateAssuresData,
  validateSharesSum,
  validateSharesSumByInsuranceType,
} from '../../../../core/utils/assure-validator.util';
import { ConfigService } from '../../../../core/services/config.service';

type SubscriptionViewPhase = 'wizard' | 'recap' | 'payment' | 'success';

const SUBSCRIPTION_DRAFT_KEY = 'saarvie.subscription.draft';

interface SubscriptionDraft {
  productId: string;
  nestedId: string;
  currentStep: number;
  formData: Record<string, unknown>;
  detailQuittances: ApiDetailQuittance[];
  assures: AssureRecord[];
  stepValues: Record<string, Record<string, unknown>>;
}

interface CachedProfileValues {
  niu: string;
  codeAssu: string | number;
  phone: string;
  teleAssu: string;
  dateNaiss: string;
  lastName: string;
  firstName: string;
}

interface RecapRow {
  label: string;
  value: string;
}

interface RecapGuaranteeRow {
  label: string;
  capital: string;
}

interface RecapRachatRow {
  year: string;
  value: string;
}

@Component({
  selector: 'app-subscription',
  templateUrl: './subscription.component.html',
  styleUrls: ['./subscription.component.scss'],
})
export class SubscriptionComponent implements OnInit, OnDestroy {
  catalog?: ApiProductCatalogItem;
  technicalProduct?: ApiNestedProduct;
  pages: ApiProductPage[] = [];
  currentStep = 0;
  stepForms: Record<string, FormGroup> = {};
  formData: Record<string, unknown> = {};
  /** Options renvoyées par l’API devis (étape FORMULE) */
  detailQuittances: ApiDetailQuittance[] = [];
  currencyLabel = 'fcfa';
  detailsIndex: number | null = null;
  viewPhase: SubscriptionViewPhase = 'wizard';
  initData: ApiInitData | null = null;
  assures: AssureRecord[] = [];
  afnResult: ApiDetailQuittance | null = null;
  beneficiaryError = '';
  cpDownloading = false;
  cpDownloadError = '';
  stepValidationError = '';
  displayValidationErrors = false;

  loading = true;
  submitting = false;
  error: string | null = null;
  infoMessage: string | null = null;
  errorModalMessage: string | null = null;
  errorModalCanRetry = false;

  private errorModalRetry: (() => void) | null = null;
  private lastPaymentReference = '';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly productApi: ProductApiService,
    private readonly souscriptionApi: SouscriptionApiService,
    private readonly authService: AuthService,
    private readonly configService: ConfigService,
  ) {}

  ngOnInit(): void {
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([params, queryParams]) => {
        const productId = params.get('productId') ?? '';
        const nestedId = queryParams.get('nested') ?? '';
        this.loadProduct(productId, nestedId);
      });

    this.authService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.pages.length) {
          this.applyProfilePrefill();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get currentPage(): ApiProductPage | undefined {
    return this.pages[this.currentStep];
  }

  get isFormuleStep(): boolean {
    return (this.currentPage?.operationType || '').toUpperCase() === 'FORMULE';
  }

  get showContinueButton(): boolean {
    return !this.isFormuleStep;
  }

  get currentForm(): FormGroup | undefined {
    const page = this.currentPage;
    return page ? this.stepForms[page.id] : undefined;
  }

  get visibleFields(): ApiProductField[] {
    return this.getVisibleFields(this.currentPage);
  }

  get isLastStep(): boolean {
    return this.currentStep >= this.pages.length - 1;
  }

  get operationLabel(): string {
    const type = (this.currentPage?.operationType || '').toUpperCase();
    if (type === 'DEVIS') {
      return 'Devis';
    }
    if (type === 'FORMULE') {
      return 'Formule';
    }
    if (type === 'AFN') {
      return 'Souscription';
    }
    return 'Étape';
  }

  get progressPercent(): number {
    if (!this.pages.length) {
      return 0;
    }
    return ((this.currentStep + 1) / this.pages.length) * 100;
  }

  get productImageUrl(): string {
    return this.technicalProduct?.image?.url || this.catalog?.image?.url || '';
  }

  get selectedQuittance(): ApiDetailQuittance | null {
    return (
      this.afnResult ||
      (this.formData['selectedQuittance'] as ApiDetailQuittance | null) ||
      null
    );
  }

  get selectedPrimeLabel(): string {
    const primtota = this.toNumber(this.selectedQuittance?.quittance?.PRIMTOTA);
    return primtota != null ? this.formatPrime(primtota) : '';
  }

  get recapPrimeTtcLabel(): string {
    const primTtc = this.quittanceField('PRIM_TTC', 'primTtc');
    if (primTtc) {
      return primTtc;
    }
    return this.selectedPrimeLabel;
  }

  get recapPolicyRows(): RecapRow[] {
    const rows: RecapRow[] = [];
    const libforco = this.quittanceField('LIBFORCO', 'libforco');
    const dateeche = this.quittanceField('DATEECHE', 'dateeche');
    const dateeffe = this.quittanceField('DATEEFFE', 'dateeffe');
    const capiassu = this.quittanceField('CAPIASSU', 'capiassu');

    if (libforco) {
      rows.push({ label: 'Formule de couverture', value: libforco });
    }
    if (dateeche) {
      rows.push({ label: "Date d'échéance", value: dateeche });
    }

    for (const row of this.buildRecapRowsForMembership('policy')) {
      if (!rows.some((existing) => existing.label === row.label)) {
        rows.push(row);
      }
    }

    if (capiassu && !rows.some((row) => row.label.toLowerCase().includes('capital'))) {
      rows.push({
        label: 'Capital assuré (FCFA)',
        value: `${this.formatRecapNumber(capiassu)} XAF`,
      });
    }
    if (dateeffe && !rows.some((row) => row.label.toLowerCase().includes('effet'))) {
      rows.push({ label: "Date de prise d'effet", value: dateeffe });
    }

    return rows;
  }

  get recapInsuredRows(): RecapRow[] {
    const profile = this.authService.getProfile();
    const rows: RecapRow[] = [];
    const lastName = profile?.lName?.trim();
    const firstName = profile?.fName?.trim();

    if (lastName) {
      rows.push({ label: 'Noms', value: lastName });
    }
    if (firstName) {
      rows.push({ label: 'Prenoms', value: firstName });
    }

    return rows;
  }

  get showRecapInsuredSection(): boolean {
    return this.recapInsuredRows.length > 0;
  }

  get paymentAmount(): number {
    return this.toNumber(this.selectedQuittance?.quittance?.PRIMTOTA) ?? 0;
  }

  get recapGuaranteeRows(): RecapGuaranteeRow[] {
    const garanties =
      this.selectedQuittance?.garanties ??
      this.detailQuittances[Number(this.formData['selectedDetailQuittanceIndex'] ?? 0)]?.garanties ??
      [];

    return garanties
      .map((garantie) => ({
        label: String(garantie.LIBEGARA || garantie.CODEGARA || '—'),
        capital: this.formatGuaranteeCapital(garantie),
      }))
      .filter((row) => row.label !== '—');
  }

  get recapRachatRows(): RecapRachatRow[] {
    return this.resolveRecapRachats()
      .map((rachat) => {
        const record = rachat as Record<string, unknown>;
        const year = String(record['LIBE__AN'] ?? record['libelleAnnee'] ?? record['AN'] ?? record['annee'] ?? '').trim();
        const rawValue = record['VR'] ?? record['vr'];
        return {
          year: this.toSentenceCase(year),
          value: this.formatRachatValue(rawValue),
        };
      })
      .filter((row) => row.year);
  }

  get recapBeneficiaryFieldDefs(): ApiProductField[] {
    const afnPage = this.pages.find((page) => (page.operationType || '').toUpperCase() === 'AFN');
    return getBottomSheetFields(afnPage?.fields ?? []);
  }

  get recapBeneficiarySections(): Array<{ rows: RecapRow[] }> {
    return this.assures
      .map((assure) => ({
        rows: this.recapBeneficiaryFieldDefs
          .filter((field) => {
            const value = assure[field.name];
            if ((field.type || '').toLowerCase() === 'toggle') {
              return parseBankDetails(value).enabled;
            }
            return value != null && value !== '';
          })
          .map((field) => ({
            label:
              (field.type || '').toLowerCase() === 'toggle'
                ? 'Prélèvement bancaire'
                : field.label || field.name,
            value: this.formatBeneficiaryValue(field, assure[field.name]),
          })),
      }))
      .filter((section) => section.rows.length > 0);
  }

  get bankOptions() {
    return mapCustomerBanks(this.initData?.banks ?? this.initData?.banque_client);
  }

  get beneficiarySectionTitle(): string {
    const title = this.technicalProduct?.['inheritor'];
    return typeof title === 'string' && title.trim() ? title.trim() : 'Bénéficiaires';
  }

  get canDownloadCp(): boolean {
    return Boolean(
      this.particularConditionId &&
        this.quittanceField('CODEINTE', 'codeInte') &&
        this.quittanceField('NUMEPOLI', 'numepoli'),
    );
  }

  get canDownloadAttestation(): boolean {
    return Boolean(
      this.attestationId &&
        this.quittanceField('CODEINTE', 'codeInte') &&
        this.quittanceField('NUMEPOLI', 'numepoli'),
    );
  }

  get policyNumber(): string {
    return String(this.selectedQuittance?.quittance?.NUMEPOLI ?? '');
  }

  get successProductName(): string {
    return (this.technicalProduct?.name || this.catalog?.name || '').trim().toUpperCase();
  }

  get successPolicyNumber(): string {
    const codeinte = this.quittanceField('CODEINTE', 'codeInte');
    const numepoli = this.quittanceField('NUMEPOLI', 'numepoli');
    if (codeinte && numepoli) {
      return `${codeinte} - ${numepoli}`;
    }
    return this.policyNumber;
  }

  get guaranteePeriodLabel(): string {
    const from = this.quittanceField('DATEEFFE', 'dateeffe');
    const to = this.quittanceField('DATEECHE', 'dateeche');
    if (!from && !to) {
      return '';
    }
    return `Du ${from || '—'} au ${to || '—'}`;
  }

  get backLink(): string[] {
    return this.catalog ? ['/produits', this.catalog.id] : ['/accueil'];
  }

  onAssuresChange(assures: AssureRecord[]): void {
    this.assures = assures;
    this.formData = { ...this.formData, assures };
    this.beneficiaryError = '';
    this.clearValidationFeedback();
  }

  clearValidationFeedback(): void {
    this.displayValidationErrors = false;
    this.stepValidationError = '';
  }

  closeErrorModal(): void {
    this.errorModalMessage = null;
    this.errorModalCanRetry = false;
    this.errorModalRetry = null;
  }

  retryErrorModal(): void {
    const retry = this.errorModalRetry;
    this.closeErrorModal();
    retry?.();
  }

  private showErrorModal(message: string, retry?: () => void): void {
    const text = (message || '').trim();
    if (!text) {
      return;
    }
    this.errorModalMessage = text;
    this.errorModalCanRetry = Boolean(retry);
    this.errorModalRetry = retry ?? null;
  }

  stepLabel(page: ApiProductPage, index: number): string {
    const type = (page.operationType || '').toUpperCase();
    if (type === 'DEVIS') {
      return 'Informations de base';
    }
    if (type === 'FORMULE') {
      return "Options d'assurance";
    }
    if (type === 'AFN') {
      return 'Validation & Paiement';
    }

    const titre = (page.titre || '').trim();
    if (titre && !/^page\s*\d+$/i.test(titre)) {
      return titre;
    }

    return `Étape ${index + 1}`;
  }

  formatOptionLabel(option: ApiFieldDataOption): string {
    const raw = this.optionLabel(option);
    const num = Number(String(raw).replace(/\s/g, ''));
    if (!Number.isNaN(num) && String(raw).match(/^\d+$/)) {
      return new Intl.NumberFormat('fr-FR').format(num);
    }
    return raw;
  }

  getVisibleFields(page?: ApiProductPage): ApiProductField[] {
    return [...(page?.fields ?? [])]
      .filter((field) => field.accessible !== false && !field.belongsToBottomSheet)
      .filter((field) => !this.isHiddenFieldType(field.type))
      .filter((field) => !['custominsured', 'twocentralizedelements'].includes(this.fieldType(field)))
      .sort(
        (a, b) =>
          (a.ordreAffichage ?? a.position ?? 0) - (b.ordreAffichage ?? b.position ?? 0)
      );
  }

  isHiddenFieldType(type?: string): boolean {
    return (type || '').toLowerCase() === 'button';
  }

  hasCustomInsured(page?: ApiProductPage): boolean {
    return (page?.fields ?? []).some(
      (field) => (field.type || '').toLowerCase() === 'custominsured' && !field.belongsToBottomSheet,
    );
  }

  hasTwoCentralizedElements(page?: ApiProductPage): boolean {
    return (page?.fields ?? []).some(
      (field) => (field.type || '').toLowerCase() === 'twocentralizedelements',
    );
  }

  fieldControl(name: string): FormControl {
    return this.currentForm?.get(name) as FormControl;
  }

  optionCode(option: ApiFieldDataOption): string {
    return String(option.CODE ?? option.code ?? '');
  }

  optionLabel(option: ApiFieldDataOption): string {
    return String(option.LABEL ?? option.label ?? this.optionCode(option));
  }

  /** Valeur stockée : CODE (comme le mobile), sauf LABEL purement numérique si pas de CODE */
  optionValue(option: ApiFieldDataOption): string {
    const code = this.optionCode(option);
    if (code) {
      return code;
    }
    const label = this.stripNumberSpaces(this.optionLabel(option));
    if (/^\d+(\.\d+)?$/.test(label)) {
      return label;
    }
    return this.optionLabel(option);
  }

  selectOptions(field: ApiProductField): ApiFieldDataOption[] {
    if (this.fieldType(field) === 'card' || this.usesFormuleOptions(field)) {
      return this.formuleOptions;
    }
    return field.dataList ?? [];
  }

  /** Options d’assurance (étape FORMULE) — priorité au devis (libforco + primtota) */
  get formuleOptions(): ApiFieldDataOption[] {
    if (this.detailQuittances.length) {
      return this.detailQuittances.map((detail, index) => {
        const quittance = detail.quittance;
        const primtota = this.toNumber(quittance?.PRIMTOTA);
        return {
          CODE: String(quittance?.FORMCOUV ?? detail.id ?? index),
          LABEL: String(quittance?.LIBFORCO ?? quittance?.FORMCOUV ?? `Option ${index + 1}`).trim(),
          primtota,
          primeLabel: this.formatPrime(primtota),
          description: (detail.garanties ?? [])
            .map((g) => g.LIBEGARA)
            .filter(Boolean)
            .join(' · '),
          guarantees: (detail.garanties ?? [])
            .map((g) => g.LIBEGARA || g.CODEGARA || '')
            .filter(Boolean),
          recommended: index === 0,
        };
      });
    }

    const formules = this.technicalProduct?.initData?.formules ?? [];
    if (formules.length) {
      return formules.map((formule) => ({
        CODE: formule.FORMCOUV || '',
        LABEL: formule.LIBFORCO || formule.FORMCOUV || 'Formule',
        description: (formule.LIST_GARANTIE ?? [])
          .map((g) => g.LIBEGARA)
          .filter(Boolean)
          .join(' · '),
        guarantees: (formule.LIST_GARANTIE ?? [])
          .map((g) => g.LIBEGARA || g.CODEGARA || '')
          .filter(Boolean),
        recommended: (formule.VALEDEFA || '').toUpperCase() === 'O',
        primtota: null,
        primeLabel: '',
      }));
    }

    if (this.technicalProduct) {
      return [
        {
          CODE: String(this.technicalProduct.code || this.technicalProduct.orsysCodeCategory || 'DEFAULT'),
          LABEL: this.technicalProduct.name,
          description: this.technicalProduct.description || this.catalog?.description || 'Formule principale',
          recommended: true,
          primtota: null,
          primeLabel: '',
        },
      ];
    }

    return [];
  }

  get firstFormuleLabel(): string {
    const first = this.formuleOptions[0];
    return first ? this.optionLabel(first) : '';
  }

  formatPrime(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) {
      return '';
    }
    return `${new Intl.NumberFormat('fr-FR').format(value)} ${this.currencyLabel}`;
  }

  get optionImageUrl(): string {
    return (
      this.technicalProduct?.image?.url ||
      this.catalog?.image?.url ||
      ''
    );
  }

  get canShowOptionDetails(): boolean {
    return this.technicalProduct?.['canShowMoreOnReceipt'] !== false;
  }

  get detailsOption(): ApiFieldDataOption | null {
    if (this.detailsIndex == null) {
      return null;
    }
    return this.formuleOptions[this.detailsIndex] ?? null;
  }

  get detailsGuarantees(): RecapGuaranteeRow[] {
    if (this.detailsIndex == null) {
      return [];
    }
    const fromDevis = this.detailQuittances[this.detailsIndex]?.garanties ?? [];
    if (fromDevis.length) {
      return fromDevis
        .map((garantie) => ({
          label: String(garantie.LIBEGARA || garantie.CODEGARA || '—'),
          capital: this.formatGuaranteeCapital(garantie),
        }))
        .filter((row) => row.label !== '—');
    }
    return ((this.detailsOption?.guarantees as string[]) ?? [])
      .filter(Boolean)
      .map((label) => ({ label, capital: '—' }));
  }

  openOptionDetails(index: number): void {
    this.detailsIndex = index;
  }

  closeOptionDetails(): void {
    this.detailsIndex = null;
  }

  async selectFormuleOption(_fieldName: string, _option: ApiFieldDataOption, index: number): Promise<void> {
    this.clearValidationFeedback();
    const page = this.currentPage;
    if (!page || !this.isFormuleStep) {
      return;
    }

    this.applyFormuleSelection(page, index);
    this.persistCurrentStep();

    if (!this.authService.isAuthenticated()) {
      this.saveSubscriptionDraft();
      this.router.navigate(['/se-connecter'], {
        queryParams: { returnUrl: this.router.url },
      });
      return;
    }

    if (!this.validateBeneficiaries(page)) {
      this.displayValidationErrors = true;
      this.stepValidationError = this.beneficiaryError;
      this.showErrorModal(this.beneficiaryError);
      return;
    }

    if (this.currentStep < this.pages.length - 1) {
      this.currentStep += 1;
      this.infoMessage = null;
      this.stepValidationError = '';
    }
  }

  usesFormuleOptions(field: ApiProductField): boolean {
    return this.isFormuleChoiceField(field, this.currentPage);
  }

  shouldShowFormuleField(field: ApiProductField, page: ApiProductPage): boolean {
    if (this.fieldType(field) === 'card') {
      return true;
    }

    if (!this.usesFormuleOptions(field) || this.fieldType(field) !== 'select') {
      return true;
    }

    const hasCard = this.getVisibleFields(page).some((item) => this.fieldType(item) === 'card');
    return !hasCard;
  }

  displayFields(page: ApiProductPage): ApiProductField[] {
    return this.getVisibleFields(page).filter((field) => this.shouldShowFormuleField(field, page));
  }

  private isFormuleChoiceField(field: ApiProductField, page?: ApiProductPage): boolean {
    const targetPage = page ?? this.currentPage;
    if ((targetPage?.operationType || '').toUpperCase() !== 'FORMULE') {
      return this.fieldType(field) === 'card';
    }

    const type = this.fieldType(field);
    return type === 'card' || type === 'select' || type === 'typeinsurance';
  }

  private getFormuleChoiceFields(page: ApiProductPage): ApiProductField[] {
    return this.getVisibleFields(page).filter((field) => this.isFormuleChoiceField(field, page));
  }

  private applyFormuleSelection(formulePage: ApiProductPage, optionIndex: number | null): void {
    const form = this.stepForms[formulePage.id];
    const fields = this.getFormuleChoiceFields(formulePage);
    if (!form || !fields.length) {
      return;
    }

    if (optionIndex === null) {
      for (const field of fields) {
        const control = form.get(field.name);
        control?.setValue('');
        control?.markAsUntouched();
      }

      const nextFormData = { ...this.formData };
      for (const field of fields) {
        nextFormData[field.name] = '';
      }
      delete nextFormData['FORMCOUV'];
      delete nextFormData['selectedQuittance'];
      delete nextFormData['selectedDetailQuittanceIndex'];
      this.formData = nextFormData;
      return;
    }

    const option = this.formuleOptions[optionIndex];
    if (!option) {
      return;
    }

    const value = this.optionValue(option);
    const patch: Record<string, unknown> = {
      ...this.formData,
      FORMCOUV: option.CODE,
      selectedQuittance: this.detailQuittances[optionIndex] ?? null,
      selectedDetailQuittanceIndex: optionIndex,
    };

    for (const field of fields) {
      form.get(field.name)?.setValue(value);
      patch[field.name] = value;
    }

    this.formData = patch;
  }

  private ensureFormuleStepReady(): void {
    const page = this.currentPage;
    if (!page || (page.operationType || '').toUpperCase() !== 'FORMULE') {
      return;
    }
    if (this.formuleOptions.length !== 1) {
      return;
    }

    const form = this.currentForm;
    if (!form) {
      return;
    }

    const fields = this.getFormuleChoiceFields(page);
    const needsSync = fields.some((field) => {
      const value = form.get(field.name)?.value;
      return value == null || value === '';
    });

    if (needsSync) {
      this.applyFormuleSelection(page, 0);
    }
  }

  fieldType(field: ApiProductField): string {
    return (field.type || 'text').toLowerCase();
  }

  isRequired(field: ApiProductField): boolean {
    return (
      !!field.obligatoire ||
      (field.constraints ?? []).some(
        (c) => (c.typeContrainte?.name || '').toLowerCase() === 'isrequired'
      )
    );
  }

  goToStep(index: number): void {
    if (index < 0 || index >= this.pages.length || this.submitting) {
      return;
    }
    if (index > this.currentStep && !this.validateCurrentStep()) {
      return;
    }
    this.persistCurrentStep();
    this.currentStep = index;
    this.infoMessage = null;
  }

  previous(): void {
    if (this.currentStep > 0) {
      this.persistCurrentStep();
      this.currentStep -= 1;
      this.infoMessage = null;
    }
  }

  async continue(): Promise<void> {
    if (this.isFormuleStep) {
      return;
    }

    this.clearValidationFeedback();
    this.ensureFormuleStepReady();

    if (!this.validateCurrentStep()) {
      this.displayValidationErrors = true;
      this.stepValidationError = this.buildStepValidationMessage();
      this.showErrorModal(this.stepValidationError);
      this.scrollToFirstInvalidField();
      return;
    }

    this.persistCurrentStep();
    const page = this.currentPage;
    if (!page) {
      return;
    }

    if (this.isLastStep) {
      if (!this.authService.isAuthenticated()) {
        this.saveSubscriptionDraft();
        this.router.navigate(['/se-connecter'], {
          queryParams: { returnUrl: this.router.url },
        });
        return;
      }

      if (!this.validateBeneficiaries(page)) {
        this.displayValidationErrors = true;
        this.stepValidationError = this.beneficiaryError;
        this.showErrorModal(this.beneficiaryError);
        return;
      }

      await this.confirmSubscription();
      return;
    }

    if (!this.validateBeneficiaries(page)) {
      this.displayValidationErrors = true;
      this.stepValidationError = this.beneficiaryError;
      this.showErrorModal(this.beneficiaryError);
      return;
    }

    if (page.canFetch && (page.operationType || '').toUpperCase() === 'DEVIS') {
      this.submitting = true;
      this.infoMessage = null;
      try {
        await this.runDevis();
        this.currentStep += 1;
        this.syncFormuleStepSelection();
      } catch (err: unknown) {
        console.error('[Subscription] devis failed', err);
        this.showErrorModal(
          extractApiErrorMessage(
            err,
            'Impossible de calculer le devis. Vérifiez les informations saisies.',
          ),
          () => {
            void this.continue();
          },
        );
      } finally {
        this.submitting = false;
      }
      return;
    }

    this.currentStep += 1;
  }

  private async runDevis(): Promise<void> {
    const dto = this.buildDevisPayload();

    if (this.configService.enableApiLogs) {
      console.log('[Subscription] devis payload', dto);
    }

    const result = await firstValueFrom(this.souscriptionApi.fetchDevis(dto));
    this.detailQuittances = Array.isArray(result) ? result : [];

    if (!this.detailQuittances.length) {
      throw new Error('Aucune option / prime n’a été renvoyée par le devis.');
    }

    if (this.detailQuittances.length === 1) {
      this.infoMessage = 'Sélectionnez la formule pour passer à l’étape suivante.';
    } else {
      this.infoMessage = `${this.detailQuittances.length} formules disponibles — choisissez-en une pour continuer.`;
    }
  }

  /**
   * Payload devis aligné mobile :
   * uniquement les champs de l’étape DEVIS + orsysIdentCategory / CODEASSU / niu.
   * (pas de productId UUID — provoque ORA-06502 buffer trop petit)
   */
  private buildDevisPayload(): Record<string, unknown> {
    const devisPage = this.pages.find(
      (p) => (p.operationType || '').toUpperCase() === 'DEVIS'
    );
    const payload: Record<string, unknown> = {};

    for (const field of this.getVisibleFields(devisPage)) {
      if (!(field.name in this.formData)) {
        continue;
      }
      payload[field.name] = this.formData[field.name];
    }

    payload['orsysIdentCategory'] = this.technicalProduct?.orsyIdentCategory ?? '';
    payload['CODEASSU'] = this.formData['CODEASSU'] ?? 0;
    payload['niu'] = this.formData['niu'] ?? '';

    return this.coerceNumericPayload(payload, devisPage);
  }

  /**
   * Construit un payload API générique (AFN etc.) à partir de formData.
   */
  private buildApiPayload(extra: Record<string, unknown> = {}): Record<string, unknown> {
    const merged: Record<string, unknown> = {
      ...this.formData,
      ...extra,
    };
    // Retirer les clés techniques web trop longues pour Orsys
    delete merged['productId'];
    delete merged['technicalProductId'];
    delete merged['selectedQuittance'];
    delete merged['selectedDetailQuittanceIndex'];
    return this.coerceNumericPayload(merged);
  }

  private coerceNumericPayload(
    data: Record<string, unknown>,
    pageScope?: ApiProductPage
  ): Record<string, unknown> {
    const fieldByName = new Map<string, ApiProductField>();
    const pages = pageScope ? [pageScope] : this.pages;
    for (const page of pages) {
      for (const field of page.fields ?? []) {
        if (field.name) {
          fieldByName.set(field.name, field);
        }
      }
    }

    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      if (value == null || value === '') {
        out[key] = value;
        continue;
      }

      if (typeof value === 'object') {
        out[key] = value;
        continue;
      }

      const field = fieldByName.get(key);
      const type = field ? this.fieldType(field) : '';

      if (type === 'date') {
        out[key] = this.toOrsysDate(value);
        continue;
      }

      if (type === 'number' || type === 'numeric') {
        const num = this.toNumber(value);
        out[key] = num ?? this.stripNumberSpaces(value);
        continue;
      }

      if (type === 'select' || type === 'typeinsurance' || type === 'card') {
        out[key] = this.coerceIfPureNumber(value);
        continue;
      }

      if (
        ['CODEASSU', 'orsysIdentCategory', 'orsyIdentCategory', 'CODECATE', 'IDENCATE'].includes(
          key
        )
      ) {
        // Mobile envoie orsysIdentCategory en string
        if (key === 'orsysIdentCategory') {
          out[key] = String(value);
          continue;
        }
        out[key] = this.coerceIfPureNumber(value);
        continue;
      }

      out[key] = this.coerceIfPureNumber(value);
    }

    return out;
  }

  /** HTML date (yyyy-MM-dd) → format Orsys/mobile dd/MM/yyyy */
  private toOrsysDate(value: unknown): string {
    const raw = String(value).trim();
    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      return `${iso[3]}/${iso[2]}/${iso[1]}`;
    }
    // déjà dd/MM/yyyy
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
      return raw;
    }
    return raw;
  }

  /** Supprime tous les espaces (y compris insécables) dans une valeur numérique */
  private stripNumberSpaces(value: unknown): string {
    return String(value)
      .replace(/[\s\u00A0\u202F\u2007\u2009\u200A\u2008]/g, '')
      .trim();
  }

  /** Convertit une string purement numérique en number (sans espaces), sinon laisse tel quel */
  private coerceIfPureNumber(value: unknown): unknown {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    const raw = this.stripNumberSpaces(value).replace(',', '.');
    if (/^-?\d+(\.\d+)?$/.test(raw)) {
      return Number(raw);
    }
    // Ex. "500 000 FCFA" → on retire au moins les espaces avant envoi
    if (typeof value === 'string' && /[\s\u00A0\u202F]/.test(value) && /\d/.test(value)) {
      return this.stripNumberSpaces(value);
    }
    return value;
  }

  private toNumber(value: unknown): number | null {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value;
    }
    const raw = this.stripNumberSpaces(value).replace(',', '.');
    if (!raw || Number.isNaN(Number(raw))) {
      return null;
    }
    return Number(raw);
  }

  private persistCurrentStep(): void {
    const form = this.currentForm;
    if (!form) {
      return;
    }
    const raw = form.getRawValue() as Record<string, unknown>;
    const coerced = this.coerceNumericPayload(raw);
    this.formData = { ...this.formData, ...coerced, assures: this.assures };
  }

  private saveSubscriptionDraft(): void {
    const productId = this.route.snapshot.paramMap.get('productId') ?? '';
    const nestedId = this.route.snapshot.queryParamMap.get('nested') ?? '';
    const stepValues: Record<string, Record<string, unknown>> = {};
    for (const page of this.pages) {
      const form = this.stepForms[page.id];
      if (form) {
        stepValues[page.id] = form.getRawValue() as Record<string, unknown>;
      }
    }

    const draft: SubscriptionDraft = {
      productId,
      nestedId,
      currentStep: this.currentStep,
      formData: this.formData,
      detailQuittances: this.detailQuittances,
      assures: this.assures,
      stepValues,
    };

    try {
      sessionStorage.setItem(SUBSCRIPTION_DRAFT_KEY, JSON.stringify(draft));
    } catch {
      // quota / mode privé
    }
  }

  private restoreSubscriptionDraft(productId: string, nestedId: string): void {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(SUBSCRIPTION_DRAFT_KEY);
    } catch {
      return;
    }
    if (!raw) {
      return;
    }

    try {
      const draft = JSON.parse(raw) as SubscriptionDraft;
      if (draft.productId !== productId || (draft.nestedId || '') !== nestedId) {
        return;
      }

      this.formData = { ...this.formData, ...draft.formData };
      this.detailQuittances = Array.isArray(draft.detailQuittances) ? draft.detailQuittances : [];
      this.assures = Array.isArray(draft.assures) ? draft.assures : [];
      this.currentStep = Number(draft.currentStep) || 0;
      Object.entries(draft.stepValues || {}).forEach(([pageId, values]) => {
        this.stepForms[pageId]?.patchValue(values);
      });
    } catch {
      // brouillon illisible
    } finally {
      sessionStorage.removeItem(SUBSCRIPTION_DRAFT_KEY);
    }
  }

  /** Pré-sélectionne l’unique formule, ou laisse le choix libre s’il y en a plusieurs */
  private syncFormuleStepSelection(): void {
    const formulePage = this.pages.find(
      (p) => (p.operationType || '').toUpperCase() === 'FORMULE'
    );
    if (!formulePage || !this.formuleOptions.length) {
      return;
    }

    if (this.formuleOptions.length === 1) {
      this.applyFormuleSelection(formulePage, 0);
      return;
    }

    this.applyFormuleSelection(formulePage, null);
  }

  trackByPageId(_: number, page: ApiProductPage): string {
    return page.id;
  }

  trackByFieldId(_: number, field: ApiProductField): string {
    return field.id;
  }

  private async confirmSubscription(): Promise<void> {
    if (!this.authService.isActivated()) {
      this.showErrorModal("Votre compte n'est pas activé");
      return;
    }

    this.submitting = true;
    this.infoMessage = null;
    this.error = null;

    try {
      const payload = this.prepareForApi();
      if (this.configService.enableApiLogs) {
        console.log('[Subscription] afn payload', payload);
      }

      this.afnResult = await firstValueFrom(this.souscriptionApi.fetchAfn(payload));
      this.formData = {
        ...this.formData,
        selectedQuittance: this.afnResult,
      };
      this.viewPhase = 'recap';
      this.infoMessage = 'Vérifiez votre récapitulatif avant validation.';
    } catch (err: unknown) {
      console.error('[Subscription] afn failed', err);
      this.showErrorModal(
        extractApiErrorMessage(
          err,
          'Impossible de finaliser la souscription. Vérifiez vos informations.',
        ),
        () => {
          void this.confirmSubscription();
        },
      );
    } finally {
      this.submitting = false;
    }
  }

  async startPayment(): Promise<void> {
    if (!this.selectedQuittance?.quittance || this.paymentAmount <= 0) {
      this.showErrorModal('Impossible de lancer le paiement pour cette souscription.');
      return;
    }
    this.infoMessage = null;
    this.viewPhase = 'payment';
  }

  async onPaymentCompleted(reference: string): Promise<void> {
    this.lastPaymentReference = reference;
    this.submitting = true;
    await this.validateAfterPayment(reference);
  }

  private async validateAfterPayment(reference: string): Promise<void> {
    if (!this.selectedQuittance?.quittance) {
      this.viewPhase = 'success';
      return;
    }

    this.submitting = true;
    try {
      const quittance = this.selectedQuittance.quittance;
      const profile = this.authService.getProfile();
      this.afnResult = await firstValueFrom(
        this.souscriptionApi.fetchAfnv({
          MONTENCA: String(quittance.PRIMTOTA ?? ''),
          phoneNumber: profile?.phone ?? '',
          orsysIdentCategory: this.technicalProduct?.orsyIdentCategory ?? '',
          niu: profile?.niu ?? this.formData['niu'] ?? '',
          policyNumber: quittance.NUMEPOLI ?? '',
          intermediateCode: quittance.CODEINTE ?? '',
          reference,
        }),
      );
      this.formData = {
        ...this.formData,
        selectedQuittance: this.afnResult,
      };
      this.viewPhase = 'success';
    } catch (err) {
      console.error('[Subscription] afnv failed', err);
      this.viewPhase = 'recap';
      this.showErrorModal(
        extractApiErrorMessage(
          err,
          'Paiement reçu, mais la validation de la souscription a échoué.',
        ),
        () => {
          void this.validateAfterPayment(this.lastPaymentReference);
        },
      );
    } finally {
      this.submitting = false;
    }
  }

  backToWizard(): void {
    this.viewPhase = 'wizard';
    this.infoMessage = null;
    this.cpDownloadError = '';
  }

  downloadConditionsParticulieres(): void {
    this.downloadPrintDocument(this.particularConditionId, 'cp', 'conditions particulières');
  }

  downloadAttestation(): void {
    this.downloadPrintDocument(this.attestationId, 'at', 'attestation');
  }

  goHome(): void {
    this.router.navigate(['/accueil']);
  }

  private validateBeneficiaries(page?: ApiProductPage): boolean {
    if (!page) {
      return true;
    }

    const fields = getBottomSheetFields(page.fields ?? []);
    const hasInsuredUi = this.hasCustomInsured(page) || page.haveListOfPolicyHolders;

    if (!hasInsuredUi) {
      return true;
    }

    if (page.haveListOfPolicyHolders && !this.assures.length) {
      this.beneficiaryError = 'Ajoutez au moins un bénéficiaire.';
      return false;
    }

    if (
      page.allBeneficiaryDataRequired &&
      !validateAssuresData(this.assures, fields, true)
    ) {
      this.beneficiaryError = 'Complétez les informations de tous les bénéficiaires.';
      return false;
    }

    if (page.canCheckPartAmount && page.allBeneficiaryDataRequired) {
      const sharesValid = this.technicalProduct?.canCheckByInsuranceType
        ? validateSharesSumByInsuranceType(this.assures, fields)
        : validateSharesSum(this.assures, fields);

      if (!sharesValid) {
        this.beneficiaryError = this.technicalProduct?.canCheckByInsuranceType
          ? 'La somme des parts vie et décès doit être égale à 100 %.'
          : 'La somme des parts doit être égale à 100 %.';
        return false;
      }
    }

    this.beneficiaryError = '';
    return true;
  }

  private prepareForApi(): Record<string, unknown> {
    const selectedQuittance = this.formData['selectedQuittance'] as ApiDetailQuittance | undefined;
    const payload = this.buildApiPayload();
    const profile = this.authService.isAuthenticated()
      ? this.getCachedProfileValues()
      : null;

    payload['niu'] = profile?.niu || payload['niu'] || '';
    payload['CODEASSU'] = profile?.codeAssu ?? payload['CODEASSU'] ?? 0;
    payload['phoneNumber'] = profile?.phone ?? payload['phoneNumber'] ?? '';
    payload['TELEASSU'] = profile?.teleAssu || payload['TELEASSU'] || '';
    payload['insuredBirthDate'] = this.toOrsysDate(
      profile?.dateNaiss || payload['insuredBirthDate'] || payload['kimiaInsuredBirthDate'] || '',
    );

    this.stripToggleFieldsFromPayload(payload);

    if (Array.isArray(payload['assures'])) {
      payload['assures'] = (payload['assures'] as AssureRecord[]).map((assure) =>
        this.sanitizeAssureForAfn(assure),
      );
    }

    this.applyOrsysSubscriberAliases(payload, profile);

    const formcouv = String(payload['FORMCOUV'] || payload['option'] || '').trim();
    const serializedQuittance = serializeDetailQuittanceForAfn(selectedQuittance, formcouv);
    if (serializedQuittance) {
      payload['selectedQuittance'] = serializedQuittance;
    }

    this.omitBlankPayloadValues(payload);
    return payload;
  }

  /**
   * Mobile n’envoie pas les champs vides (formData sparse).
   * Le web persiste `getRawValue()` donc `DateEffet: ""` partait à l’AFN,
   * et Bora transmettait `DATEEFFE=""` à Orsys → 500.
   */
  private omitBlankPayloadValues(payload: Record<string, unknown>): void {
    for (const [key, value] of Object.entries(payload)) {
      if (key === 'selectedQuittance' || key === 'assures') {
        continue;
      }
      if (value == null || value === '') {
        delete payload[key];
      }
    }
  }

  /**
   * `boraAfn` utilise `parseSuscriber` qui lit `insuredName`, `insuredNickName`
   * et `beneficiaryBirthDate` — pas les noms de champs produit / profil.
   */
  private applyOrsysSubscriberAliases(
    payload: Record<string, unknown>,
    profile: CachedProfileValues | null,
  ): void {
    const firstAssure = Array.isArray(payload['assures'])
      ? (payload['assures'] as AssureRecord[])[0]
      : undefined;

    payload['insuredName'] = this.firstNonEmpty(
      payload['insuredName'],
      this.valueFromFlaggedField(payload, 'isSubscriberName'),
      profile?.lastName,
      firstAssure?.['beneficiaryName'],
    );
    payload['insuredNickName'] = this.firstNonEmpty(
      payload['insuredNickName'],
      this.valueFromFlaggedField(payload, 'isSubscriberFirstName'),
      profile?.firstName,
    );

    const birth = this.toOrsysDate(
      this.firstNonEmpty(
        payload['beneficiaryBirthDate'],
        this.valueFromFlaggedField(payload, 'isSubscriberBirthdate'),
        payload['kimiaInsuredBirthDate'],
        payload['insuredBirthDate'],
        firstAssure?.['beneficiaryBirthDate'],
        firstAssure?.['insuredBirthDate'],
        profile?.dateNaiss,
      ),
    );
    if (birth) {
      payload['beneficiaryBirthDate'] = birth;
      payload['kimiaInsuredBirthDate'] = payload['kimiaInsuredBirthDate'] || birth;
      payload['insuredBirthDate'] = payload['insuredBirthDate'] || birth;
    }
  }

  private valueFromFlaggedField(payload: Record<string, unknown>, flag: string): unknown {
    const field = this.pages
      .flatMap((page) => page.fields ?? [])
      .find((item) => item.name && this.fieldFlag(item, flag));
    return field?.name ? payload[field.name] : undefined;
  }

  private firstNonEmpty(...values: unknown[]): string {
    for (const value of values) {
      if (value == null) {
        continue;
      }
      const text = String(value).trim();
      if (text) {
        return text;
      }
    }
    return '';
  }

  /** Mobile `BankDetailsWidget` n’écrit pas dans formData : on n’envoie pas ces objets à l’AFN. */
  private stripToggleFieldsFromPayload(payload: Record<string, unknown>): void {
    for (const name of this.toggleFieldNames()) {
      delete payload[name];
    }

    for (const [key, value] of Object.entries(payload)) {
      if (isBankDetailsValue(value)) {
        delete payload[key];
      }
    }
  }

  private toggleFieldNames(): string[] {
    return this.pages
      .flatMap((page) => page.fields ?? [])
      .filter((field) => this.fieldType(field) === 'toggle' && field.name)
      .map((field) => field.name);
  }

  /**
   * Aligné mobile `prepareForApi` + contrat Bora `parseBeneficiaryCapi`
   * (`typeInsurance.toString()` NPE si la clé est absente).
   */
  private sanitizeAssureForAfn(assure: AssureRecord): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    const toggleNames = new Set(this.toggleFieldNames());

    for (const [key, value] of Object.entries(assure)) {
      if (toggleNames.has(key) || isBankDetailsValue(value)) {
        continue;
      }
      if (value != null && typeof value === 'object') {
        continue;
      }
      out[key] = value;
    }

    out['CODEASSU'] = 0;

    const typeField = this.pages
      .flatMap((page) => page.fields ?? [])
      .find((field) => field.belongsToBottomSheet && this.fieldType(field) === 'typeinsurance');
    const typeRaw =
      out['typeInsurance'] ??
      (typeField?.name ? out[typeField.name] : undefined) ??
      assure['typeInsurance'] ??
      (typeField?.name ? assure[typeField.name] : undefined);
    out['typeInsurance'] = typeRaw != null && typeRaw !== '' ? String(typeRaw) : 'vie';

    const birthField = this.pages
      .flatMap((page) => page.fields ?? [])
      .find((field) => field.belongsToBottomSheet && this.fieldFlag(field, 'isSubscriberBirthdate'));
    const birthRaw =
      out['beneficiaryBirthDate'] ??
      (birthField?.name ? out[birthField.name] : undefined) ??
      assure['beneficiaryBirthDate'] ??
      assure['insuredBirthDate'];
    const birth = this.toOrsysDate(birthRaw);
    if (birth) {
      out['beneficiaryBirthDate'] = birth;
    }

    return out;
  }

  private getCachedProfileValues(): CachedProfileValues {
    const profile = this.authService.getProfile();
    const codeAssu = profile?.codeClient?.trim();
    const codePays = (profile?.codePays || '').trim();
    const phone = (profile?.phone || '').trim();

    return {
      niu: profile?.niu?.trim() ?? '',
      codeAssu: codeAssu ? codeAssu : 0,
      phone,
      teleAssu: `${codePays}${phone}`,
      dateNaiss: profile?.dateNaiss?.trim() ?? '',
      lastName: (profile?.lName || profile?.name || '').trim(),
      firstName: (profile?.fName || '').trim(),
    };
  }

  private formatRecapValue(field: ApiProductField, raw: unknown): string {
    if (this.fieldType(field) === 'date') {
      return this.toOrsysDate(raw);
    }
    if (this.fieldType(field) === 'select') {
      const opt = this.selectOptions(field).find(
        (option) => this.optionValue(option) === String(raw),
      );
      return opt ? this.formatOptionLabel(opt) : String(raw);
    }
    return String(raw);
  }

  private get particularConditionId(): string {
    const value = this.technicalProduct?.['particularConditionId'];
    return value != null ? String(value).trim() : '';
  }

  private get attestationId(): string {
    const value = this.technicalProduct?.['attestationId'];
    return value != null ? String(value).trim() : '';
  }

  private downloadPrintDocument(idenetat: string, suffix: string, label: string): void {
    if (!idenetat || this.cpDownloading) {
      return;
    }

    const codeinte = this.quittanceField('CODEINTE', 'codeInte');
    const numepoli = this.quittanceField('NUMEPOLI', 'numepoli');
    if (!codeinte || !numepoli) {
      return;
    }

    this.cpDownloading = true;
    this.cpDownloadError = '';

    this.souscriptionApi.fetchPrint(idenetat, codeinte, numepoli).subscribe({
      next: (base64Data) => {
        try {
          this.openPdfFromBase64(
            base64Data,
            `${suffix}_${this.catalog?.name ?? 'contrat'}_${numepoli}.pdf`,
          );
        } catch (err) {
          console.error('[Subscription] print open failed', err);
          this.showErrorModal(`Impossible d’ouvrir le document ${label}.`);
        }
      },
      error: (err) => {
        console.error('[Subscription] print download failed', err);
        this.showErrorModal(
          extractApiErrorMessage(err, `Impossible de télécharger ${label}.`),
          () => this.downloadPrintDocument(idenetat, suffix, label),
        );
        this.cpDownloading = false;
      },
      complete: () => {
        this.cpDownloading = false;
      },
    });
  }

  private quittanceField(upperKey: string, lowerKey: string): string {
    const quittance = this.selectedQuittance?.quittance;
    if (!quittance) {
      return '';
    }
    const value = quittance[upperKey] ?? quittance[lowerKey];
    return value != null && value !== '' ? String(value) : '';
  }

  private buildRecapRowsForMembership(membership: string): RecapRow[] {
    const fields = this.getRecapFieldsByMembership(membership);
    return this.buildRecapRows(fields);
  }

  private getRecapFieldsByMembership(membership: string): ApiProductField[] {
    return (this.pages ?? [])
      .flatMap((page) => page.fields ?? [])
      .filter(
        (field) =>
          this.membershipMatches(field, membership) &&
          field.presentInRecap !== false &&
          !field.belongsToBottomSheet &&
          !['card', 'custominsured', 'button'].includes(this.fieldType(field)),
      )
      .sort(
        (a, b) =>
          (a.ordreAffichage ?? a.position ?? 0) - (b.ordreAffichage ?? b.position ?? 0),
      );
  }

  private membershipMatches(field: ApiProductField, membership: string): boolean {
    const normalizedFieldMembership = (field.membership || '')
      .toLowerCase()
      .trim()
      .replace(/[\s-]+/g, '_');
    const normalizedTarget = membership.toLowerCase().trim();

    const aliases: Record<string, string[]> = {
      policy_holder: ['policy_holder', 'policyholder', 'assure', 'assuré', 'insured', 'holder'],
      policy: ['policy', 'police'],
    };

    const allowed = aliases[normalizedTarget] ?? [normalizedTarget];
    return allowed.includes(normalizedFieldMembership);
  }

  private buildRecapRows(fields: ApiProductField[]): RecapRow[] {
    const rows: RecapRow[] = [];
    for (const field of fields) {
      const raw = this.formData[field.name];
      if (raw == null || raw === '') {
        continue;
      }
      rows.push({
        label: field.label || field.name,
        value: this.formatRecapValue(field, raw),
      });
    }
    return rows;
  }

  private formatGuaranteeCapital(garantie: ApiGarantie): string {
    const record = garantie as Record<string, unknown>;
    const raw = record['CAPIASSU'] ?? record['capiassu'] ?? record['capiAssu'];
    if (raw == null || raw === '') {
      return '—';
    }
    return `${this.formatRecapNumber(raw)} ${this.currencyLabel}`;
  }

  private resolveRecapRachats(): ApiRachat[] {
    const fromAfn = this.asRachatList(this.afnResult?.rachats);
    if (fromAfn.length) {
      return fromAfn;
    }

    const selected = this.formData['selectedQuittance'] as ApiDetailQuittance | undefined;
    const fromSelected = this.asRachatList(selected?.rachats);
    if (fromSelected.length) {
      return fromSelected;
    }

    const index = Number(this.formData['selectedDetailQuittanceIndex'] ?? 0);
    return this.asRachatList(this.detailQuittances[index]?.rachats);
  }

  private asRachatList(value: unknown): ApiRachat[] {
    return Array.isArray(value) ? (value as ApiRachat[]) : [];
  }

  private formatRachatValue(raw: unknown): string {
    if (raw == null || raw === '') {
      return '—';
    }
    return `${this.formatRecapNumber(raw)} ${this.currencyLabel}`;
  }

  private toSentenceCase(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }

  private formatRecapNumber(value: unknown): string {
    const num = this.toNumber(value);
    if (num != null) {
      return new Intl.NumberFormat('fr-FR').format(num);
    }
    return String(value);
  }

  private formatBeneficiaryValue(field: ApiProductField, raw: unknown): string {
    if ((field.type || '').toLowerCase() === 'toggle') {
      return formatBankDetails(raw);
    }
    if (field['isParente'] === true && this.initData?.parentes?.length) {
      const code = String(raw ?? '');
      const parent = this.initData.parentes.find(
        (option) =>
          String(option.code ?? option.CODE ?? '') === code ||
          String(option.label ?? option.LABEL ?? '') === code,
      );
      if (parent) {
        return String(parent.label ?? parent.LABEL ?? code).toUpperCase();
      }
    }
    return this.formatRecapValue(field, raw);
  }

  private openPdfFromBase64(base64Data: string, fileName: string): void {
    const cleaned = base64Data.replace(/^data:application\/pdf;base64,/, '').trim();
    const binary = atob(cleaned);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.target = '_blank';
    anchor.rel = 'noopener';
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  private applyProfilePrefill(): void {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    const profile = this.authService.getProfile();
    if (!profile) {
      return;
    }

    const birthDateHtml = this.profileBirthDateForInput(profile.dateNaiss);
    const prefill: Record<string, unknown> = {
      CODEASSU: profile.codeClient?.trim() ? profile.codeClient : 0,
      niu: profile.niu ?? '',
      phoneNumber: profile.phone ?? '',
      TELEASSU: profile.phone ?? '',
    };

    if (birthDateHtml) {
      prefill['insuredBirthDate'] = birthDateHtml;
      prefill['kimiaInsuredBirthDate'] = birthDateHtml;
    }

    this.formData = { ...this.formData, ...prefill };

    for (const page of this.pages) {
      const form = this.stepForms[page.id];
      if (!form) {
        continue;
      }

      for (const field of this.getVisibleFields(page)) {
        const control = form.get(field.name);
        if (!control || control.value) {
          continue;
        }

        if (field.name in prefill) {
          control.setValue(prefill[field.name]);
          continue;
        }

        if (birthDateHtml && this.isBirthDateField(field)) {
          control.setValue(birthDateHtml);
          this.formData[field.name] = birthDateHtml;
          continue;
        }

        if (this.fieldFlag(field, 'isSubscriberName')) {
          const lastName = (profile.lName || profile.name || '').trim();
          if (lastName) {
            control.setValue(lastName);
            this.formData[field.name] = lastName;
          }
          continue;
        }

        if (this.fieldFlag(field, 'isSubscriberFirstName')) {
          const firstName = (profile.fName || '').trim();
          if (firstName) {
            control.setValue(firstName);
            this.formData[field.name] = firstName;
          }
          continue;
        }

        if (this.fieldFlag(field, 'isSubscriberAdress')) {
          const address = (profile.address || '').trim();
          if (address) {
            control.setValue(address);
            this.formData[field.name] = address;
          }
        }
      }
    }

    this.prefillPrincipalAssure(profile);
  }

  /**
   * Assuré principal auto-ajouté (mobile addDate + setMoreInformationsInSubscriberInsured).
   * Produits type Flash Millénium : canCheckByInsuranceType → vie / part 100 % / parenté P.
   */
  private prefillPrincipalAssure(profile: UserProfile): void {
    const page = this.getBeneficiariesPage();
    if (!page || !this.technicalProduct) {
      return;
    }

    const dateField = (page.fields ?? []).find((field) => this.fieldFlag(field, 'hasAddingDatesButton'));
    const canPrefill = Boolean(dateField?.id) || Boolean(this.technicalProduct.canCheckByInsuranceType);
    if (!canPrefill) {
      return;
    }

    const birthApi = this.toApiDate(profile.dateNaiss);
    if (!birthApi) {
      return;
    }

    let assures = [...this.assures];
    if (!assures.length) {
      const assure: AssureRecord = {
        id: Date.now(),
        index: '0',
        isSubscriber: true,
      };
      if (dateField?.name) {
        assure[dateField.name] = birthApi;
      }
      assures = [assure];
    }

    const subscriberIndex = assures.findIndex((item) => item['isSubscriber'] === true);
    if (subscriberIndex < 0) {
      this.assures = assures;
      this.formData = { ...this.formData, assures };
      return;
    }

    const subscriber: AssureRecord = { ...assures[subscriberIndex] };
    const lastName = (profile.lName || profile.name || '').trim();
    const firstName = (profile.fName || '').trim();
    const fullName = `${lastName} ${firstName}`.trim();

    const nameField = this.findBottomSheetField(page, 'isInsureName');
    const firstNameField = this.findBottomSheetField(page, 'isInsureFirstname');
    const birthField = this.findBottomSheetField(page, 'isSubscriberBirthdate');
    const shareField = this.findBottomSheetField(page, 'isAShare');
    const parentField = this.findBottomSheetField(page, 'isParente');

    if (nameField?.name && fullName && !subscriber[nameField.name]) {
      subscriber[nameField.name] = fullName;
    }
    if (firstNameField?.name && firstName && !subscriber[firstNameField.name]) {
      subscriber[firstNameField.name] = firstName;
    }
    if (birthField?.name && !subscriber[birthField.name]) {
      subscriber[birthField.name] = birthApi;
    }

    if (this.technicalProduct.canCheckByInsuranceType) {
      if (subscriber['typeInsurance'] == null || subscriber['typeInsurance'] === '') {
        subscriber['typeInsurance'] = 'vie';
      }
      if (subscriber['beneficiaryShare'] == null || subscriber['beneficiaryShare'] === '') {
        subscriber['beneficiaryShare'] = '100';
      }
      if (subscriber['beneficiaryParentalBinding'] == null || subscriber['beneficiaryParentalBinding'] === '') {
        subscriber['beneficiaryParentalBinding'] = 'P';
      }
      if (shareField?.name && (subscriber[shareField.name] == null || subscriber[shareField.name] === '')) {
        subscriber[shareField.name] = '100';
      }
      if (parentField?.name && (subscriber[parentField.name] == null || subscriber[parentField.name] === '')) {
        subscriber[parentField.name] = 'P';
      }
    }

    const typeInsuranceField = (page.fields ?? []).find(
      (field) => field.belongsToBottomSheet && this.fieldType(field) === 'typeinsurance',
    );
    if (typeInsuranceField?.name && (subscriber[typeInsuranceField.name] == null || subscriber[typeInsuranceField.name] === '')) {
      subscriber[typeInsuranceField.name] = subscriber['typeInsurance'] ?? 'vie';
    }

    assures[subscriberIndex] = subscriber;
    this.assures = assures;
    this.formData = { ...this.formData, assures };
  }

  private getBeneficiariesPage(): ApiProductPage | undefined {
    return this.pages.find(
      (page) =>
        this.hasCustomInsured(page) ||
        (page.fields ?? []).some((field) => this.fieldFlag(field, 'hasAddingDatesButton')),
    );
  }

  private findBottomSheetField(page: ApiProductPage, flag: string): ApiProductField | undefined {
    return (page.fields ?? []).find((field) => field.belongsToBottomSheet && this.fieldFlag(field, flag));
  }

  private fieldFlag(field: ApiProductField | undefined, flag: string): boolean {
    return field?.[flag] === true;
  }

  private profileBirthDateForInput(dateNaiss?: string | null): string | null {
    const raw = dateNaiss?.trim();
    if (!raw) {
      return null;
    }
    return this.toHtmlDate(raw);
  }

  private isBirthDateField(field: ApiProductField): boolean {
    if (this.fieldType(field) !== 'date') {
      return false;
    }

    const name = field.name.toLowerCase();
    const label = (field.label || '').toLowerCase();
    return (
      name.includes('birth') ||
      name.includes('naiss') ||
      label.includes('naissance') ||
      label.includes('birth')
    );
  }

  /** Profil → dd/MM/yyyy (format API / mobile). */
  private toApiDate(value?: string | null): string | null {
    const raw = value?.trim();
    if (!raw) {
      return null;
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
      return raw;
    }
    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      return `${iso[3]}/${iso[2]}/${iso[1]}`;
    }
    return null;
  }

  /** dd/MM/yyyy ou yyyy-MM-dd → yyyy-MM-dd pour input[type=date] */
  private toHtmlDate(value: string): string {
    const ddmmyyyy = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (ddmmyyyy) {
      return `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`;
    }

    const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      return `${iso[1]}-${iso[2]}-${iso[3]}`;
    }

    return value;
  }

  private buildStepValidationMessage(): string {
    const messages: string[] = [];
    const page = this.currentPage;

    for (const field of page ? this.getVisibleFields(page) : this.visibleFields) {
      messages.push(...this.getFieldErrorMessages(field));
    }

    if (messages.length) {
      return messages.length === 1
        ? messages[0]
        : `${messages[0]} (${messages.length} champ(s) à corriger)`;
    }

    return 'Veuillez remplir tous les champs obligatoires.';
  }

  private scrollToFirstInvalidField(): void {
    queueMicrotask(() => {
      const invalid = document.querySelector('.field--error');
      invalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  private getFieldErrorMessages(field: ApiProductField): string[] {
    const control = this.currentForm?.get(field.name);
    if (!control?.errors) {
      return [];
    }

    const messages: string[] = [];

    if (control.errors['required']) {
      const required = (field.constraints ?? []).find(
        (c) => (c.typeContrainte?.name || '').toLowerCase() === 'isrequired',
      );
      messages.push(required?.errorMessage || `${field.label || field.name} est obligatoire.`);
    }

    if (control.errors['constraints']) {
      messages.push(...(control.errors['constraints'] as string[]));
    }

    if (control.errors['max']) {
      messages.push('Valeur trop élevée.');
    }

    if (control.errors['min']) {
      messages.push('Valeur trop faible.');
    }

    if (control.errors['numeric']) {
      messages.push('Veuillez saisir un nombre valide.');
    }

    return messages;
  }

  private validateCurrentStep(): boolean {
    const form = this.currentForm;
    if (!form) {
      return true;
    }
    form.markAllAsTouched();
    return form.valid;
  }

  private loadProduct(productId: string, nestedProductId = ''): void {
    this.loading = true;
    this.error = null;
    this.catalog = undefined;
    this.technicalProduct = undefined;
    this.pages = [];
    this.stepForms = {};
    this.currentStep = 0;
    this.formData = {};
    this.detailQuittances = [];
    this.viewPhase = 'wizard';
    this.assures = [];
    this.afnResult = null;
    this.beneficiaryError = '';
    this.stepValidationError = '';
    this.displayValidationErrors = false;
    this.initData = null;

    if (!productId) {
      this.loading = false;
      this.error = 'Identifiant produit manquant.';
      return;
    }

    this.productApi
      .getById(productId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (catalog) => {
          this.loading = false;
          if (!catalog) {
            this.error = 'Produit introuvable.';
            return;
          }

          this.catalog = catalog;
          const products = catalog.products ?? [];
          const technical = nestedProductId
            ? products.find((item) => item.id === nestedProductId)
            : products[0];
          if (!technical?.pages?.length) {
            this.error = nestedProductId
              ? 'Formule introuvable pour ce produit.'
              : 'Aucun parcours de souscription n’est configuré pour ce produit.';
            return;
          }

          this.technicalProduct = technical;
          this.pages = [...technical.pages].sort(
            (a, b) => (a.ordre ?? 0) - (b.ordre ?? 0)
          );
          this.formData = {
            orsysIdentCategory: technical.orsyIdentCategory,
            productId: catalog.id,
            technicalProductId: technical.id,
          };
          this.buildStepForms();
          this.restoreSubscriptionDraft(productId, nestedProductId);
          this.applyProfilePrefill();
          this.loadInitData();
        },
        error: (err) => {
          console.error('[Subscription] load failed', err);
          this.loading = false;
          this.error = 'Impossible de charger le parcours de souscription.';
        },
      });
  }

  private loadInitData(): void {
    this.souscriptionApi
      .fetchInitData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.initData = this.normalizeInitData(data);
        },
        error: () => {
          this.initData = null;
        },
      });
  }

  private buildStepForms(): void {
    for (const page of this.pages) {
      const group: Record<string, FormControl> = {};
      for (const field of this.getVisibleFields(page)) {
        group[field.name] = this.fb.control(
          this.defaultFieldValue(field, page),
          this.buildValidators(field, page)
        );
      }
      this.stepForms[page.id] = this.fb.group(group);
    }
  }

  private normalizeInitData(data: ApiInitData | null | undefined): ApiInitData | null {
    if (!data) {
      return null;
    }
    const record = data as Record<string, unknown>;
    return {
      ...data,
      banks: mapCustomerBanks(record['banque_client'] ?? record['banks'] ?? data.banque_client ?? data.banks),
    };
  }

  private defaultFieldValue(
    field: ApiProductField,
    page: ApiProductPage,
  ): string | number | boolean | ReturnType<typeof emptyBankDetails> {
    if (this.isFormuleChoiceField(field, page) && this.formuleOptions.length === 1) {
      return this.optionValue(this.formuleOptions[0]);
    }
    if (this.fieldType(field) === 'card') {
      return '';
    }
    if (this.fieldType(field) === 'toggle') {
      return emptyBankDetails();
    }
    return field.valeurParDefaut ?? '';
  }

  private buildValidators(field: ApiProductField, page?: ApiProductPage): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    const isFormuleChoice = this.isFormuleChoiceField(field, page);

    if (this.fieldType(field) !== 'toggle' && (this.isRequired(field) || isFormuleChoice)) {
      validators.push(Validators.required);
    }

    if (this.fieldType(field) !== 'toggle') {
      validators.push(this.fieldConstraintValidator(field));
    }
    return validators;
  }

  private fieldConstraintValidator(field: ApiProductField): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === '' || control.value == null) {
        return null;
      }

      const numericValue = this.resolveNumericForConstraint(field, control.value);
      const messages: string[] = [];

      for (const constraint of field.constraints ?? []) {
        const name = (constraint.typeContrainte?.name || '').toLowerCase();
        const message = constraint.errorMessage?.trim();

        if (name === 'maximum' && constraint.value != null && numericValue != null) {
          if (numericValue > Number(constraint.value)) {
            messages.push(message || `La valeur ne peut pas dépasser ${constraint.value}.`);
          }
        }

        if ((name === 'minum' || name === 'minimum') && constraint.value != null && numericValue != null) {
          if (numericValue < Number(constraint.value)) {
            messages.push(message || `La valeur ne peut pas être inférieure à ${constraint.value}.`);
          }
        }
      }

      if ((this.fieldType(field) === 'number' || this.fieldType(field) === 'numeric') && numericValue == null) {
        messages.push('Veuillez saisir un nombre valide.');
      }

      return messages.length ? { constraints: messages } : null;
    };
  }

  /** Pour un select, min/max portent sur le LABEL numérique, pas sur le CODE (ex. 500K) */
  private resolveNumericForConstraint(field: ApiProductField, value: unknown): number | null {
    const type = this.fieldType(field);
    if (type === 'select' || type === 'typeinsurance') {
      const opt = this.selectOptions(field).find(
        (o) => this.optionValue(o) === String(value) || this.optionCode(o) === String(value)
      );
      if (opt) {
        const fromLabel = this.toNumber(opt.LABEL ?? opt.label);
        if (fromLabel != null) {
          return fromLabel;
        }
      }
    }
    return this.toNumber(value);
  }

  fieldErrors(field: ApiProductField): string[] {
    const control = this.fieldControl(field.name);
    if (!control || !control.errors) {
      return [];
    }

    if (!control.touched && !this.displayValidationErrors) {
      return [];
    }

    return this.getFieldErrorMessages(field);
  }

  /** @deprecated use fieldErrors */
  fieldError(field: ApiProductField): string | null {
    return this.fieldErrors(field)[0] ?? null;
  }
}
